import { defineStore } from 'pinia'
import { ref, computed, watch, watchEffect, type Ref } from 'vue'

export interface InAppNotification {
  id: string
  type: 'invite-shopping' | 'invite-book' | 'invite-recipe' | 'shopping' | 'system' | 'info'
  title: string
  message: string
  time: number
  read: boolean
  url?: string
  data?: any
}

export type MainSwipeMode = 'intelligent' | 'tab' | 'page'

export const useSystemStore = defineStore('system', () => {
  // --- TOASTS ---
  const toasts = ref<Array<{
    text: string
    type: 'success' | 'error' | 'warning' | 'info'
    id: number
    typeId?: string
    action?: { label: string; callback: () => void }
  }>>([])

  let toastIdCounter = 0
  const toastTimers = new Map<number, ReturnType<typeof setTimeout>>()

  const showToast = (
    text: string,
    type: 'success' | 'error' | 'warning' | 'info' = 'success',
    action?: { label: string; callback: () => void },
    typeId?: string
  ) => {
    const resolvedTypeId = typeId || `${type}:${text}`

    // Check if an existing toast has the exact same typeId / content
    const existingIndex = toasts.value.findIndex(t => (t.typeId || `${t.type}:${t.text}`) === resolvedTypeId)

    if (existingIndex !== -1) {
      const existing = toasts.value[existingIndex]
      if (toastTimers.has(existing.id)) {
        clearTimeout(toastTimers.get(existing.id)!)
        toastTimers.delete(existing.id)
      }
      // Remove existing toast immediately so Vue transition group re-animates
      toasts.value.splice(existingIndex, 1)

      // Re-insert after brief delay to re-fire entry animation
      setTimeout(() => {
        const newId = ++toastIdCounter
        const newToast = { text, type, id: newId, typeId: resolvedTypeId, action }
        toasts.value.push(newToast)
        const timeout = action ? 6000 : 4000
        const timer = setTimeout(() => dismissToast(newId), timeout)
        toastTimers.set(newId, timer)
      }, 50)
      return
    }

    if (action) {
      const actionToasts = toasts.value.filter(t => t.action)
      if (actionToasts.length >= 2) {
        const oldest = actionToasts[0]
        dismissToast(oldest.id)
      }
    }

    const id = ++toastIdCounter
    toasts.value.push({ text, type, id, typeId: resolvedTypeId, action })
    const timeout = action ? 6000 : 4000
    const timer = setTimeout(() => {
      dismissToast(id)
    }, timeout)
    toastTimers.set(id, timer)
  }

  const dismissToast = (id: number) => {
    if (toastTimers.has(id)) {
      clearTimeout(toastTimers.get(id)!)
      toastTimers.delete(id)
    }
    toasts.value = toasts.value.filter(t => t.id !== id)
  }

  // --- CONFIRMATION DIALOG ---
  const confirmDialogState = ref<{
    isOpen: boolean
    title: string
    message: string
    confirmLabel?: string
    cancelLabel?: string
    resolve: ((val: boolean) => void) | null
  }>({
    isOpen: false,
    title: '',
    message: '',
    confirmLabel: 'Bestätigen',
    cancelLabel: 'Abbrechen',
    resolve: null
  })

  const confirmAction = (title: string, message: string, confirmLabel = 'Bestätigen', cancelLabel = 'Abbrechen'): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      confirmDialogState.value = {
        isOpen: true,
        title,
        message,
        confirmLabel,
        cancelLabel,
        resolve
      }
    })
  }

  const handleConfirmResult = (result: boolean) => {
    if (confirmDialogState.value.resolve) {
      confirmDialogState.value.resolve(result)
    }
    confirmDialogState.value.isOpen = false
  }

  // --- DELETE WITH REPLACEMENT DIALOG ---
  const deleteWithReplacementState = ref<{
    isOpen: boolean
    type: 'article' | 'unit' | 'articleCategory' | 'recipeCategory'
    item: { id: string; name: string }
    affectedRecipes: Array<{ id: string; name: string }>
    affectedShoppingItems: Array<{ id: string; articleName: string }>
    affectedArticles?: Array<{ id: string; name: string }>
    resolve: ((result: { action: 'replace' | 'delete' | 'cancel'; replacementId?: string }) => void) | null
  }>({
    isOpen: false,
    type: 'article',
    item: { id: '', name: '' },
    affectedRecipes: [],
    affectedShoppingItems: [],
    affectedArticles: [],
    resolve: null
  })

  const handleDeleteWithReplacementResult = (result: { action: 'replace' | 'delete' | 'cancel'; replacementId?: string }) => {
    if (deleteWithReplacementState.value.resolve) {
      deleteWithReplacementState.value.resolve(result)
    }
    deleteWithReplacementState.value.isOpen = false
    deleteWithReplacementState.value.resolve = null
  }

  // --- NOTIFICATION CENTER ---
  const isNotificationsModalOpen = ref(false)

  const loadNotificationsHistory = (): InAppNotification[] => {
    try {
      const raw = localStorage.getItem('app_notifications_history')
      if (raw) {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) return parsed
      }
    } catch (e) {
      console.error('Failed to load notifications history', e)
    }
    return []
  }

  const notificationsHistory = ref<InAppNotification[]>(loadNotificationsHistory())

  const saveNotificationsHistory = () => {
    try {
      localStorage.setItem('app_notifications_history', JSON.stringify(notificationsHistory.value.slice(0, 50)))
    } catch (e) {
      console.error('Failed to save notifications history', e)
    }
  }

  const addNotification = (notif: {
    id?: string
    type?: InAppNotification['type']
    title: string
    message: string
    time?: number
    read?: boolean
    url?: string
    data?: any
  }): InAppNotification => {
    const newNotif: InAppNotification = {
      id: notif.id || `notif-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type: notif.type || 'info',
      title: notif.title,
      message: notif.message,
      time: notif.time || Date.now(),
      read: notif.read || false,
      url: notif.url,
      data: notif.data
    }
    const existingIndex = notificationsHistory.value.findIndex(n => n.id === newNotif.id)
    if (existingIndex >= 0) {
      notificationsHistory.value[existingIndex] = newNotif
    } else {
      notificationsHistory.value.unshift(newNotif)
    }
    if (notificationsHistory.value.length > 50) {
      notificationsHistory.value = notificationsHistory.value.slice(0, 50)
    }
    saveNotificationsHistory()
    return newNotif
  }

  const markNotificationRead = (id: string) => {
    const item = notificationsHistory.value.find(n => n.id === id)
    if (item && !item.read) {
      item.read = true
      saveNotificationsHistory()
    }
  }

  const markAllNotificationsRead = () => {
    notificationsHistory.value.forEach(n => { n.read = true })
    saveNotificationsHistory()
  }

  const clearNotificationHistory = () => {
    notificationsHistory.value = []
    saveNotificationsHistory()
  }

  const deleteNotification = (id: string) => {
    notificationsHistory.value = notificationsHistory.value.filter(n => n.id !== id)
    saveNotificationsHistory()
  }

  // --- THEME & DARK MODE ---
  const getInitialThemeMode = (): 'light' | 'dark' | 'auto' => {
    const saved = localStorage.getItem('themeMode')
    if (saved === 'light' || saved === 'dark' || saved === 'auto') {
      return saved
    }
    const oldDarkMode = localStorage.getItem('darkMode')
    if (oldDarkMode === 'true') return 'dark'
    if (oldDarkMode === 'false') return 'light'
    return 'auto'
  }

  const isBrowser = typeof window !== 'undefined'
  const systemIsDark = ref(isBrowser ? window.matchMedia('(prefers-color-scheme: dark)').matches : false)
  if (isBrowser) {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', (e) => {
      systemIsDark.value = e.matches
    })
  }

  const themeMode = ref<'light' | 'dark' | 'auto'>(getInitialThemeMode())

  const darkMode = computed(() => {
    if (themeMode.value === 'auto') {
      return systemIsDark.value
    }
    return themeMode.value === 'dark'
  })

  // Synchronously update DOM classes & data-theme
  if (isBrowser) {
    watchEffect(() => {
      const isDark = darkMode.value
      document.documentElement.classList.toggle('dark-mode', isDark)
      document.documentElement.classList.toggle('light-mode', !isDark)
      document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light')
      document.body.classList.toggle('dark-mode', isDark)
      document.body.classList.toggle('light-mode', !isDark)
      document.body.setAttribute('data-theme', isDark ? 'dark' : 'light')
    }, { flush: 'sync' })

    watch(themeMode, (val) => {
      localStorage.setItem('themeMode', val)
      localStorage.setItem('darkMode', String(darkMode.value))
      const link = document.querySelector('link[rel="manifest"]')
      if (link) {
        link.setAttribute('href', darkMode.value ? '/manifest-dark.webmanifest' : '/manifest.webmanifest')
      }
    })
  }

  // --- LOCAL STORAGE REFS ---
  function useLocalStorageRef<T>(key: string, defaultValue: T): Ref<T> {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null
    let initial: any = defaultValue
    if (stored !== null) {
      if (typeof defaultValue === 'boolean') {
        initial = stored === 'true'
      } else if (typeof defaultValue === 'number') {
        const num = Number(stored)
        initial = !isNaN(num) ? num : defaultValue
      } else {
        initial = stored
      }
    }
    const state = ref(initial) as Ref<T>
    watch(state, (val) => {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(key, String(val))
      }
    }, { flush: 'sync' })
    return state
  }

  const compactTiles = useLocalStorageRef<boolean>('compactTiles', false)
  const quickAddToCart = useLocalStorageRef<boolean>('quickAddToCart', false)
  // Migration from 24h to 12h
  if (typeof localStorage !== 'undefined') {
    try {
      if (localStorage.getItem('autoDeleteCheckedItems12h') === null && localStorage.getItem('autoDeleteCheckedItems24h') !== null) {
        localStorage.setItem('autoDeleteCheckedItems12h', localStorage.getItem('autoDeleteCheckedItems24h')!)
      }
    } catch {
      // Ignore localStorage access errors
    }
  }
  const autoDeleteCheckedItems12h = useLocalStorageRef<boolean>('autoDeleteCheckedItems12h', true)
  const autoDeleteCheckedItems24h = computed({
    get: () => autoDeleteCheckedItems12h.value,
    set: (val: boolean) => { autoDeleteCheckedItems12h.value = val }
  })
  const startPage = useLocalStorageRef<string>('startPage', 'shopping-list-if-unchecked')
  const mainSwipeMode = useLocalStorageRef<MainSwipeMode>('mainSwipeMode', 'intelligent')
  const keepScreenAwake = useLocalStorageRef<boolean>('keepScreenAwake', true)
  const keepScreenAwakeUseDuration = useLocalStorageRef<boolean>('keepScreenAwakeUseDuration', true)
  const invertCategoryMultiSelect = useLocalStorageRef<boolean>('invertCategoryMultiSelect', false)
  const speechVoiceURI = useLocalStorageRef<string>('speechVoiceURI', '')
  const speechRate = useLocalStorageRef<number>('speechRate', 1.0)
  const speechPitch = useLocalStorageRef<number>('speechPitch', 1.0)
  const supportUrl = useLocalStorageRef<string>('supportUrl', 'https://buymeacoffee.com/kochbucheinkaufsliste')

  if (!supportUrl.value || supportUrl.value === 'https://buymeacoffee.com') {
    supportUrl.value = 'https://buymeacoffee.com/kochbucheinkaufsliste'
  }

  const updateSupportUrl = async (url: string) => {
    supportUrl.value = url
    const [{ doc, setDoc }, { db }] = await Promise.all([
      import('firebase/firestore'),
      import('../firebase')
    ])
    await setDoc(doc(db, 'system_config', 'app_config'), { supportUrl: url }, { merge: true })
  }

  // --- IMAGE API KEYS ---
  const pixabayApiKey = ref('')
  const pexelsApiKey = ref('')
  const unsplashAccessKey = ref('')
  const unsplashAppId = ref('')
  const unsplashSecret = ref('')

  const updateImageApiKeys = async (keys: {
    pixabayApiKey: string
    pexelsApiKey: string
    unsplashAccessKey: string
    unsplashAppId: string
    unsplashSecret: string
  }) => {
    const [{ doc, setDoc }, { db }] = await Promise.all([
      import('firebase/firestore'),
      import('../firebase')
    ])
    await setDoc(doc(db, 'system_config', 'image_api'), keys)
  }

  // --- SETTINGS TAB TRIGGER ---
  const settingsAvailableTabs = ref<string[]>(['general', 'shoppingList', 'recipes', 'watch', 'data', 'system'])
  const settingsTabTrigger = ref<{ delta: number; timestamp: number } | null>(null)
  const triggerSettingsTabOffset = (delta: number) => {
    settingsTabTrigger.value = { delta, timestamp: Date.now() }
  }

  return {
    // Toasts
    toasts,
    showToast,
    dismissToast,
    // Confirmation dialog
    confirmDialogState,
    confirmAction,
    handleConfirmResult,
    // Delete with replacement dialog
    deleteWithReplacementState,
    handleDeleteWithReplacementResult,
    // Notification Center
    isNotificationsModalOpen,
    notificationsHistory,
    addNotification,
    markNotificationRead,
    markAllNotificationsRead,
    clearNotificationHistory,
    deleteNotification,
    // Theme
    themeMode,
    darkMode,
    systemIsDark,
    // Settings refs
    useLocalStorageRef,
    compactTiles,
    quickAddToCart,
    autoDeleteCheckedItems12h,
    autoDeleteCheckedItems24h,
    startPage,
    mainSwipeMode,
    keepScreenAwake,
    keepScreenAwakeUseDuration,
    invertCategoryMultiSelect,
    speechVoiceURI,
    speechRate,
    speechPitch,
    supportUrl,
    updateSupportUrl,
    // Image APIs
    pixabayApiKey,
    pexelsApiKey,
    unsplashAccessKey,
    unsplashAppId,
    unsplashSecret,
    updateImageApiKeys,
    // Settings Tabs
    settingsAvailableTabs,
    settingsTabTrigger,
    triggerSettingsTabOffset
  }
})
